import React from 'react';
import { Lock } from 'lucide-react';
import { useFeatureAccess, FeaturePermissions } from '@/contexts/FeatureAccessContext';
import { getPageLabel, getFeatureLabel } from '@/lib/subscriptions/subscriptionConfig';

interface FeatureGuardProps {
    requiredFeature?: keyof FeaturePermissions;
    pageId?: string;
    children: React.ReactNode;
}

/**
 * ComponentUpgradePrompt
 * Inline component-level access denied prompt
 * Used when FeatureGuard doesn't have a pageId
 */
const ComponentUpgradePrompt: React.FC<{ feature: keyof FeaturePermissions }> = ({ feature }) => {
    return (
        <div className="flex flex-col items-center justify-center p-8 bg-background-light-elevated dark:bg-background-dark-elevated rounded-lg border border-border-light dark:border-border-dark-subtle">
            <div className="space-y-4 text-center">
                <Lock className="h-12 w-12 text-primary-500 mx-auto" />
                <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                    Feature Restricted
                </h3>
                <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                    {getFeatureLabel(feature)} is not included in your current plan.
                </p>
                <button
                    onClick={() => (window.location.href = '/app/settings')}
                    className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors text-sm"
                >
                    Upgrade Plan
                </button>
            </div>
        </div>
    );
};

/**
 * PageUpgradePrompt
 * Full-page access denied prompt
 * Shows more detailed message about what feature is needed
 */
const PageUpgradePrompt: React.FC<{
    page: string;
    requiredFeature: keyof FeaturePermissions;
}> = ({ page, requiredFeature }) => {
    return (
        <div className="flex items-center justify-center min-h-screen bg-background-light dark:bg-background-dark">
            <div className="max-w-md mx-auto text-center space-y-6 p-8 bg-background-light-elevated dark:bg-background-dark-elevated rounded-lg border border-border-light dark:border-border-dark-subtle">
                <div className="space-y-4">
                    <Lock className="h-16 w-16 text-primary-500 mx-auto" />
                    <div>
                        <h2 className="text-2xl font-bold text-text-primary-light dark:text-text-primary-dark mb-2">
                            Page Access Restricted
                        </h2>
                        <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
                            Access to <strong>{getPageLabel(page)}</strong> requires the{' '}
                            <strong>{getFeatureLabel(requiredFeature)}</strong> feature, which is not included in your current plan.
                        </p>
                    </div>
                </div>

                <div className="space-y-3">
                    <button
                        onClick={() => (window.location.href = '/app/settings')}
                        className="w-full px-4 py-2 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
                    >
                        View Upgrade Options
                    </button>
                    <button
                        onClick={() => window.history.back()}
                        className="w-full px-4 py-2 border border-border-light dark:border-border-dark rounded-lg font-medium text-text-secondary-light dark:text-text-secondary-dark hover:bg-background-light dark:hover:bg-background-dark transition-colors"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * FeatureGuard
 * Main guard component for protecting routes/components
 *
 * Usage:
 * <FeatureGuard requiredFeature="threatProfiling" pageId="threat-profile">
 *   <YourComponent />
 * </FeatureGuard>
 */
const FeatureGuard: React.FC<FeatureGuardProps> = ({ requiredFeature, pageId, children }) => {
    const { hasFeature } = useFeatureAccess();

    // If no feature is required, show content (public access)
    if (!requiredFeature) {
        return <>{children}</>;
    }

    // Check if user has the required feature
    const hasAccess = hasFeature(requiredFeature);

    // If user doesn't have access
    if (!hasAccess) {
        // If we have a page ID, show full-page upgrade prompt
        if (pageId) {
            return <PageUpgradePrompt page={pageId} requiredFeature={requiredFeature} />;
        }
        // Otherwise, show inline component-level prompt
        return <ComponentUpgradePrompt feature={requiredFeature} />;
    }

    // User has access, show content
    return <>{children}</>;
};

export default FeatureGuard;